import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { Button, IconButton, Tooltip } from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import AssignmentReturnedOutlinedIcon from "@mui/icons-material/AssignmentReturnedOutlined";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import moment from 'moment'

const initials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    return ((parts[0] ? parts[0][0] : "") +
        (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
};

/* dates arrive both as real dates and as already-formatted strings */
const showDate = (value) => {
    if (!value) return "--";
    const parsed = moment(new Date(value));
    return parsed.isValid() ? parsed.format("DD MMM YYYY") : String(value);
};

const ViewAssetsModal = ({ isStatusModel = false, modelHandle = () => { }, selectedEmployee = null, getEmployeeList = () => { } }) => {
    const [assets, setAssets] = useState([]);
    const { fetchData } = useAxios();
    const [checkedAssets, setCheckedAssets] = useState({});
    const [AddNewAssets, setAddNewAssets] = useState({});
    const [isSaveBtnActive, setIsSaveBtnActive] = useState(false);

    const issued = selectedEmployee?.Assets || [];
    const held = issued.filter((row) => !row?.isReturned);
    const returned = issued.filter((row) => row?.isReturned);

    const handleCheckboxChange = (assetName) => {
        setCheckedAssets((prevState) => ({
            ...prevState,
            [assetName]: !prevState[assetName],
        }));

        if (!checkedAssets[assetName]) {
            setAddNewAssets((prevState) => ({
                ...prevState,
                [assetName]: [],
            }));
        } else {
            setAddNewAssets((prevState) => {
                const updatedState = { ...prevState };
                delete updatedState[assetName];
                return updatedState;
            });
        }
    };

    const handleAddSerialInput = (assetName) => {
        setAddNewAssets((prevState) => ({
            ...prevState,
            [assetName]: [...(prevState[assetName] || []), ""],
        }));
    };

    const handleRemoveSerialInput = (assetName, index) => {
        setAddNewAssets((prevState) => ({
            ...prevState,
            [assetName]: (prevState[assetName] || []).filter((_, i) => i !== index),
        }));
    };

    const handleSerialNumberChange = (assetName, index, value) => {
        setAddNewAssets((prevState) => {
            const updatedSerials = [...(prevState[assetName] || [])];
            updatedSerials[index] = { 'ItemName': assetName, 'serial_no': value, };
            return {
                ...prevState,
                [assetName]: updatedSerials,
            };
        });
    };

    const handleSaveAssets = async () => {
        const newAssetsKeys = Object.keys(AddNewAssets)
        let requiredAssetsFormat = []
        newAssetsKeys && newAssetsKeys?.length > 0 && newAssetsKeys?.forEach((key) => {
            requiredAssetsFormat = [...requiredAssetsFormat, ...AddNewAssets[key]]
        })
        const payload = {
            userId: selectedEmployee?._id,
            assets: requiredAssetsFormat,
        };

        const response = await fetchData({
            url: URLS.updateEmpAssets,
            method: "put",
            body: payload,
        });

        /* fetchData resolves on failure too, so the response has to be read.
           A try/catch here would never fire. */
        if (response?.success === false) {
            toast.error(response?.message || "Failed to save assets. Please try again.");
            return;
        }

        toast.success("Assets saved");
        modelHandle();
        getEmployeeList();
    };

    const getDisabledStatus = () => {
        const newAssetsKeys = Object.keys(AddNewAssets)
        let requiredAssetsFormat = []
        newAssetsKeys && newAssetsKeys?.length > 0 && newAssetsKeys?.forEach((key) => {
            requiredAssetsFormat = [...requiredAssetsFormat, ...AddNewAssets[key]]
        })
        const ValidAssetsData = requiredAssetsFormat?.filter((asset) => asset && asset?.serial_no !== '' && asset?.serial_no !== null)
        setIsSaveBtnActive(ValidAssetsData?.length > 0 ? true : false)
    }

    const markReturned = async (id) => {
        const response = await fetchData({
            url: URLS.returnAsset,
            method: "put",
            body: { "userId": selectedEmployee?._id, "assetId": id },
        });

        if (response?.success === false) {
            toast.error(response?.message || "Could not mark this as returned");
            return;
        }

        toast.success("Marked as returned");
        modelHandle();
        getEmployeeList();
    }

    useEffect(() => {
        const assetsList = async () => {
            const response = await fetchData({
                url: URLS.getAssetsList,
                method: "get",
            });
            setAssets(response?.Items || []);
        };
        assetsList();
    }, []);

    useEffect(() => {
        getDisabledStatus();
    }, [AddNewAssets]);

    /* What is left after the rows already queued in this popup, so the count
       drops as serial numbers are added rather than only after saving. */
    const freeStock = (asset) => {
        const base = asset.available ?? Math.max((asset.totalStock || 0) - (asset.issued || 0), 0);
        const queued = (AddNewAssets[asset.ItemName] || []).filter((row) => row?.serial_no).length;
        return Math.max(base - queued, 0);
    };

    const renderIssued = (row) => (
        <li key={row?._id} className="wh-issued">
            <div className="wh-issued-main">
                <span className="wh-pill">{row?.item}</span>
                <span className="wh-issued-serial">{row?.serial_no || "No serial number"}</span>
            </div>

            <div className="wh-issued-meta">
                <span><em>Issued</em> {showDate(row?.issued_date)}</span>
                <span><em>By</em> {row?.provideBy || "--"}</span>
                {row?.isReturned && <span><em>Returned</em> {showDate(row?.ReceivedOn)}</span>}
                {row?.isReturned && <span><em>Received by</em> {row?.ReceivedBy || "--"}</span>}
            </div>

            <div className="wh-issued-action">
                {row?.isReturned
                    ? <span className="wh-pill wh-pill-quiet">Returned</span>
                    : (
                        <Tooltip title="Mark as returned" placement="top" arrow>
                            <Button variant="outlined" startIcon={<AssignmentReturnedOutlinedIcon />}
                                onClick={() => markReturned(row?._id)}>
                                Returned
                            </Button>
                        </Tooltip>
                    )}
            </div>
        </li>
    );

    return (
        <Modal size="lg" show={isStatusModel} onHide={modelHandle} backdrop="static" className="wh-modal">
            <Modal.Header closeButton>
                <Modal.Title>Assets</Modal.Title>
            </Modal.Header>

            <Modal.Body className="assets-modal-body-cls">

                <div className="wh-profile-hero">
                    <div className="wh-avatar">{initials(selectedEmployee?.employeeName)}</div>
                    <div className="wh-profile-id">
                        <h5>{selectedEmployee?.employeeName || "--"}</h5>
                        <p>{selectedEmployee?.designation || "Designation not set"}</p>
                    </div>
                    <span className="wh-pill wh-profile-code">{selectedEmployee?.employeeCode}</span>
                </div>

                <div className="wh-stats">
                    <div><b>{held.length}</b><span>Currently held</span></div>
                    <div><b>{returned.length}</b><span>Returned</span></div>
                    <div><b>{issued.length}</b><span>Ever issued</span></div>
                </div>

                <h6 className="wh-section-title">
                    <Inventory2OutlinedIcon />Currently held
                </h6>
                {held.length > 0
                    ? <ul className="wh-issued-list">{held.map(renderIssued)}</ul>
                    : <p className="wh-section-empty">Nothing is issued to this employee yet.</p>}

                {returned.length > 0 && (
                    <>
                        <h6 className="wh-section-title">
                            <AssignmentReturnedOutlinedIcon />Returned
                        </h6>
                        <ul className="wh-issued-list">{returned.map(renderIssued)}</ul>
                    </>
                )}

                <h6 className="wh-section-title">
                    <AddBoxOutlinedIcon />Issue more
                </h6>
                <div className="asset-list-container">
                    <ul className="asset-list">
                        {assets.map((eachAsset, i) => {
                            const left = freeStock(eachAsset);
                            const soldOut = left === 0;

                            return (
                            <li key={eachAsset.ItemName + i} className="asset-item">
                                <div className="d-flex justify-content-between align-items-start">
                                    <label className="asset-label">
                                        <input
                                            type="checkbox"
                                            className="asset-checkbox"
                                            checked={!!checkedAssets[eachAsset.ItemName]}
                                            disabled={soldOut && !checkedAssets[eachAsset.ItemName]}
                                            onChange={() => handleCheckboxChange(eachAsset.ItemName)}
                                        />
                                        <span className="custom-checkbox"></span>
                                        <span className="asset-naming">
                                            <span className="wh-pill">{eachAsset.ItemName}</span>
                                            {[eachAsset.make, eachAsset.model, eachAsset.processor,
                                                eachAsset.ram, eachAsset.modelYear].filter(Boolean).length > 0 &&
                                                <em>{[eachAsset.make, eachAsset.model, eachAsset.processor,
                                                    eachAsset.ram, eachAsset.modelYear].filter(Boolean).join(' · ')}</em>}
                                            <span className={`wh-pill wh-pill-sm ${soldOut ? 'wh-pill-danger' : left <= 2 ? 'wh-pill-warn' : 'wh-pill-good'}`}>
                                                {soldOut ? 'None left' : `${left} available`}
                                            </span>
                                        </span>
                                    </label>
                                    {!!checkedAssets[eachAsset.ItemName] && (
                                        <Tooltip title={soldOut ? "No stock left" : "Add a serial number"} placement="top" arrow>
                                            <span>
                                                <IconButton className="add-asset-btn-cls" disabled={soldOut}
                                                    onClick={() => handleAddSerialInput(eachAsset.ItemName)}>
                                                    <AddCircleRoundedIcon className="wh-add-icon" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>

                                {checkedAssets[eachAsset.ItemName] && (
                                    <div className="asset-details">
                                        {AddNewAssets[eachAsset.ItemName]?.length > 0
                                            ? AddNewAssets[eachAsset.ItemName].map((serial, index) => (
                                                <div key={index} className="serial-row">
                                                    <input
                                                        type="text"
                                                        value={serial?.['serial_no'] || ''}
                                                        placeholder="Enter serial number"
                                                        onChange={(e) => handleSerialNumberChange(eachAsset.ItemName, index, e.target.value)}
                                                        className="serial-input"
                                                    />
                                                    <IconButton className="add-asset-btn-cls wh-danger"
                                                        aria-label="Remove"
                                                        onClick={() => handleRemoveSerialInput(eachAsset.ItemName, index)}>
                                                        <CloseRoundedIcon />
                                                    </IconButton>
                                                </div>
                                            ))
                                            : <p className="wh-section-empty">Use + to add a serial number.</p>}
                                    </div>
                                )}
                            </li>
                            );
                        })}
                    </ul>
                </div>

            </Modal.Body>

            <Modal.Footer>
                <Button variant="contained" color="inherit" onClick={modelHandle}>
                    Close
                </Button>
                <Button variant="contained" onClick={handleSaveAssets} disabled={!isSaveBtnActive}>
                    Save
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ViewAssetsModal;
